import { FieldWrapper } from "@progress/kendo-react-form";
import { Input } from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import isString from "lodash-es/isString";
import last from "lodash-es/last";
import * as React from "react";
import { useCallback } from "react";
import { renderTooltipText } from "@/components/Kendo/ToolTip/renderTooltipText";
import FormLabel from "./FormLabel";
import IconHint from "./IconHint";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface FormFileInputProps {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  hintIcon?: boolean;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  /** When true, onChange receives the File object directly; otherwise the original event */
  processValue?: boolean;
}

const FILE_INPUT_CLASS = "tlx-file-input";

export const FormFileInput: React.FC<FormFileInputProps> = ({
  name,
  validator,
  label,
  hint,
  hintIcon = false,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
  processValue = false,
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const showHintText = showHint && !hintIcon;
  const showHintIcon = showHint && hintIcon;
  const inputId = name;
  const labelId = label ? `${inputId}_label` : "";
  const hintId = showHint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";

  const value = field.value as File | string | null | undefined;
  const fileName: string | undefined = value
    ? last((isString(value) ? value : value.name).split("\\"))
    : undefined;

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (processValue) {
        field.onChange(event.target.files?.[0] ?? null);
        return;
      }
      field.onChange(event);
    },
    [field, processValue],
  );

  const handleClear = useCallback(() => {
    const input = document.querySelector(
      `input.${FILE_INPUT_CLASS}#${inputId}`,
    );
    if (input instanceof HTMLInputElement) {
      input.value = "";
    }
    field.onChange(null);
  }, [field, inputId]);

  return (
    <FieldWrapper className="tlx-file-input-field-wrapper">
      <Tooltip
        anchorElement="target"
        parentTitle={true}
        style={{ maxWidth: 400 }}
        showCallout={false}
        content={renderTooltipText}
      >
        <FormLabel
          id={labelId}
          editorId={inputId}
          editorValid={!fieldState.error}
          editorDisabled={!!disabled}
          floating={floatingLabel}
          floatLabel={floatLabel}
          label={label}
          value={fileName?.length ? fileName : undefined}
          placeholder={placeholder}
          optional={optional}
        >
          <div className={`k-form-field-wrap ${inputId}`}>
            <Input
              valid={!fieldState.error}
              disabled
              autoComplete="off"
              ariaDescribedBy={`${hintId} ${errorId}`}
              style={{ paddingRight: 20 }}
              value={fileName ?? ""}
            />
            <div className="tlx-file-input-wrapper">
              {fileName ? (
                <span
                  className="tlx-file-input-clear k-icon k-font-icon k-i-close-outline"
                  onClick={handleClear}
                />
              ) : (
                <label className="tlx-file-input-label" htmlFor={inputId}>
                  <span className="k-icon k-font-icon k-i-upload" /> Upload
                </label>
              )}
              <input
                hidden
                id={inputId}
                name={name}
                className={FILE_INPUT_CLASS}
                disabled={disabled}
                onChange={handleFileChange}
                type="file"
              />
            </div>
            {showHintText && <Hint id={hintId}>{hint}</Hint>}
            {showHintIcon && <IconHint hint={hint} iconWidth={14} />}
            {showError && (
              <Error id={errorId}>{fieldState.error?.message}</Error>
            )}
          </div>
        </FormLabel>
      </Tooltip>
    </FieldWrapper>
  );
};

export default FormFileInput;
